import { useThreadKeyedResource } from '#plugin-chat/hooks/useThreadKeyedResource.ts';
import {
  createThreadHistoryAdapter,
  type ThreadHistoryAdapterWithSnapshot,
} from '#plugin-chat/lib/transport/threadHistoryAdapter.ts';

/**
 * Hook returning a {@link ThreadHistoryAdapterWithSnapshot} whose object
 * identity is **stable for the lifetime of one thread session**.
 *
 * Identity stability is load-bearing for the persistence subscribe in
 * the vendored `useExternalHistory` (`@assistant-ui/react-ai-sdk`):
 *
 * ```
 * const formatAdapter = useMemo(
 *   () => historyAdapter.withFormat(...),
 *   [historyAdapter, ...]
 * );
 * useEffect(() => {
 *   const unsub = runtime.thread.subscribe(/* schedules setTimeout(0) persist *\/);
 *   return () => { unsub(); clearTimeout(persistTimerRef.current); };
 * }, [formatAdapter, ...]);
 * ```
 *
 * If `historyAdapter` is a fresh object on every render of
 * `useChatThreadRuntime`, `formatAdapter` changes too, the subscribe
 * effect tears down between schedule and fire, and the pending
 * persist `setTimeout(0)` is cleared — the user message is silently
 * dropped and a reload finds an empty thread (the cancelled-interrupt
 * / mid-turn-refresh data-loss bug).
 *
 * A bare `const historyAdapter = createThreadHistoryAdapter(id);`
 * inline depends on the React Compiler to memoize the call. The
 * compiler may bail out for surrounding-code reasons, making the
 * invariant unreliable in practice — the bug reproduces
 * intermittently. {@link useThreadKeyedResource} hoists the guarantee
 * out of the compiler's hands (see that hook for the full `useState` +
 * render-time reset, NOT `useMemo`, rationale).
 *
 * Switching threads (a different `threadId` than the one currently
 * held) re-creates the adapter during render — before any child reads
 * the new value — so the underlying `atlas-thread-messages:<threadId>`
 * storage key follows the active thread without an effect round-trip
 * or a stale frame.
 */
export function useThreadHistoryAdapter(threadId: string): ThreadHistoryAdapterWithSnapshot {
  return useThreadKeyedResource(threadId, () => createThreadHistoryAdapter(threadId));
}
